<template>
  <div class="ui-calendar-workspace">
    <app-filter-toolbar>
      <app-select
        v-model="departmentFilter"
        design="new"
        :options="departmentOptions"
        placeholder="Department"
        aria-label="Department"
        @input="fetchItems"
      />
      <app-select
        v-model="positionFilter"
        design="new"
        :options="positionOptions"
        placeholder="Position"
        aria-label="Position"
        @input="fetchItems"
      />
      <div class="ui-date-field date-picker-wrapper">
        <app-date-range-picker
          id="leave_schedule_dates"
          v-model="filterDates"
          :custom-shortcuts="customShortcuts"
          :shortcut="$route.query.from_date && $route.query.to_date ? null : 'next30Days'"
          format="YYYY-MM-DD"
          formatted="ll"
          label="Dates"
          aria-label="Dates"
          overlay
          no-header
          no-label
          locale="en"
          range
          no-clear-button
          @is-hidden="onDatesHidden"
        />
        <div v-if="datePickerError" class="date-picker-wrapper__error">Choose an end date</div>
      </div>
      <template #selection>
        <app-filters
          v-model="statusFilter"
          design="new"
          refined
          weight="medium"
          :filters="statusOptions"
          name="leaveScheduleStatus"
          aria-label="Status"
          @change="fetchItems"
        />
      </template>
      <template #actions>
        <details class="ui-legend">
          <summary v-tooltip="{ content: 'Legend', placement: 'left', offset: 8, classes: 'tooltip--new' }" class="ui-legend__button ui-legend__button--icon" aria-label="Legend">
            <i class="pi pi-list" aria-hidden="true" />
          </summary>
          <div class="ui-legend__panel">
            <strong>Leave type</strong>
            <ul>
              <li v-for="item in visibleLegendItems" :key="item.code">
                <i :style="legendStyle(item.color)"></i>
                {{ item.name }}
              </li>
            </ul>
            <div class="ui-legend__note">
              <i></i>
              Dashed border means requested
            </div>
          </div>
        </details>
      </template>
      <template v-if="hasFilters" #clear>
        <app-clear-filters-button design="new" icon-only @clear="clearFilters" />
      </template>
    </app-filter-toolbar>
    <app-content-skeleton v-if="loading" layout="block" label="Loading leave schedule" />
    <app-error-state v-else-if="loadError" :message="loadError" @retry="fetchItems" />
    <app-no-results v-else-if="schedule && !schedule.instances.length" design="new" title="No Results" text="" />
    <div
      v-else-if="schedule"
      class="ui-calendar-surface"
    >
      <app-schedule
        design="new"
        :schedule="schedule"
        text-tooltips
        :need_search="false"
        :drag-from-header-only="true"
        :stacked-period-labels="true"
        :grouped-period-months="true"
        :fill-available-height="true"
        sidebar-label="Employee / Department"
        @event-click="openEvent"
      />
    </div>

    <modal
      name="leave-schedule-detail"
      :width="560"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :click-to-close="!actionLoading"
      class="ui-modal modal-not-overflow"
      draggable=".app-modal-header__copy"
      @closed="onDetailClosed"
    >
      <section class="ui-modal__content" aria-labelledby="leave-schedule-detail-title">
        <app-modal-header design="new" :title="detailTitle" title-id="leave-schedule-detail-title" @close="closeDetail" />
        <div v-if="detailLoading" class="ui-modal__body ui-loading-center"><app-spinner design="new" /></div>
        <div v-else-if="detailEvent" class="ui-modal__body ui-record-detail">
          <div class="ui-record-heading">
            <div>
              <span>Employee</span>
              <router-link :to="{ name: 'employees-profile', params: { id: detailEvent.employee.id } }">
                {{ detailEvent.employee.full_name }}
              </router-link>
              <small>{{ employeePosition }}</small>
            </div>
            <app-status-badge design="new" compact :label="statusName" :tone="statusTone" />
          </div>

          <div class="ui-period-summary">
            <div><span>From</span><strong>{{ formatDate(detailEvent.from_date) }}</strong></div>
            <span aria-hidden="true">→</span>
            <div><span>To</span><strong>{{ formatDate(detailEvent.to_date) }}</strong></div>
            <b>{{ detailEvent.days }} day{{ detailEvent.days === 1 ? '' : 's' }}</b>
          </div>

          <div v-if="detailEvent.kind === 'change_dates'" class="ui-record-note">
            <span>Current dates (kept until approved)</span>
            <p>{{ formatDate(detailEvent.old_from_date) }} → {{ formatDate(detailEvent.old_to_date) }}</p>
          </div>

          <sales-leave-annual v-if="annual" :annual="annual" />

          <div class="ui-fact-grid">
            <div><span>Leave type</span><strong>{{ detailEvent.leave_type.name }}</strong></div>
            <div v-if="detailEvent.requested_at">
              <span>Requested</span>
              <strong :title="formatDateTime(detailEvent.requested_at)">{{ relativeDate(detailEvent.requested_at) }}</strong>
            </div>
          </div>

          <div class="ui-document-status" :class="`ui-document-status--${proofPresentation.state}`">
            <div>
              <span>Supporting document</span>
              <strong>{{ proofPresentation.name }}</strong>
              <small v-if="proofPresentation.description">{{ proofPresentation.description }}</small>
            </div>
            <a
              v-if="detailEvent.proof && detailEvent.proof.url"
              :href="detailEvent.proof.url"
              target="_blank"
              rel="noopener noreferrer"
            >Open document</a>
          </div>

          <div v-if="detailEvent.reason" class="ui-record-note">
            <span>Comment</span>
            <p>{{ detailEvent.reason }}</p>
          </div>

          <app-form-group v-if="rejectMode" design="new" label="Rejection reason" required>
            <app-textarea
              v-model="rejectionReason"
              design="new"
              placeholder="Rejection reason"
              :error="rejectSubmitted && !rejectionReason.trim()"
            />
            <template #error><div v-if="rejectSubmitted && !rejectionReason.trim()">Required field</div></template>
          </app-form-group>

          <footer v-if="hasDetailActions" class="ui-record-actions">
            <template v-if="!rejectMode">
              <app-button
                v-if="detail.permissions.reject"
                design="new"
                theme="error"
                type="button"
                :disabled="actionLoading"
                @click.native="rejectMode = true"
              >Reject</app-button>
              <app-button
                v-if="detail.permissions.approve"
                ref="approveButton"
                design="new"
                theme="success"
                type="button"
                :disabled="actionLoading"
                @click.native="approveRequest"
              >Approve</app-button>
            </template>
            <template v-else>
              <app-button design="new" theme="dark" type="button" :disabled="actionLoading" @click.native="rejectMode = false">Back</app-button>
              <app-button
                ref="rejectButton"
                design="new"
                theme="error"
                type="button"
                :disabled="actionLoading"
                @click.native="rejectRequest"
              >Confirm rejection</app-button>
            </template>
          </footer>
        </div>
      </section>
    </modal>
  </div>
</template>

<script>
import moment from 'moment/moment'
import AppSchedule from '@/components/AppSchedule.vue'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import SalesLeaveAnnual from '@/views/employees/sales-leave/SalesLeaveAnnual'
import {
  getEmployeesDepartmentList,
  getEmployeesLeaveScheduleEventDetail,
  getEmployeesLeavesCalendarV2,
  getEmployeesPositionList,
  getEmployeesSalesLeaveScheduleEventDetail,
  getEmployeesSalesLeavesCalendarV2,
  postEmployeesLeaveRequestApprove,
  postEmployeesLeaveRequestReject,
  postEmployeesSalesLeaveRequestApprove,
  postEmployeesSalesLeaveRequestReject,
} from '@/http'
import { formatRelativeDate } from '@/views/crm/task-tracker/leadFlowDate'
import {
  LEGEND_ITEMS,
  STATUS_OPTIONS,
  buildLeaveSchedule,
  colorToRgba,
  createDefaultDates,
  getProofPresentation,
} from '@/views/employees/leaves/leaveSchedule'

function scheduleDatesFromQuery(query) {
  if (typeof query.from_date !== 'string' || typeof query.to_date !== 'string') return createDefaultDates()
  const start = moment(query.from_date, 'YYYY-MM-DD', true)
  const end = moment(query.to_date, 'YYYY-MM-DD', true)
  if (start.isValid() && end.isValid() && !end.isBefore(start, 'day')) {
    return { start: start.format('YYYY-MM-DD'), end: end.format('YYYY-MM-DD') }
  }
  return createDefaultDates()
}

export default {
  name: 'EmployeesLeavesSchedule',
  components: { AppSchedule, SalesLeaveAnnual, AppContentSkeleton, AppErrorState },
  data() {
    return {
      loading: false,
      loadError: '',
      filterDates: scheduleDatesFromQuery(this.$route.query),
      datePickerError: false,
      customShortcuts: [
        { key: 'next30Days', label: 'Next 30 days', value: () => ({ start: moment(), end: moment().add(30, 'days') }) },
        { key: 'next60Days', label: 'Next 60 days', value: () => ({ start: moment(), end: moment().add(60, 'days') }) },
        { key: 'next90Days', label: 'Next 90 days', value: () => ({ start: moment(), end: moment().add(90, 'days') }) },
      ],
      statusFilter: STATUS_OPTIONS.some(option => option.value === this.$route.query.status) ? this.$route.query.status : STATUS_OPTIONS[0].value,
      statusOptions: STATUS_OPTIONS,
      departmentFilter: null,
      departmentOptions: [],
      positionFilter: null,
      positionOptions: [],
      legendItems: LEGEND_ITEMS,
      schedule: null,
      detail: null,
      detailSourceType: null,
      detailLoading: false,
      actionLoading: false,
      rejectMode: false,
      rejectSubmitted: false,
      rejectionReason: '',
      fetchRequestId: 0,
      detailRequestId: 0,
      isActive: true,
    }
  },
  computed: {
    hasFilters() {
      const dates = this.filterDates || {}
      const defaults = createDefaultDates()
      return Boolean(
        this.departmentFilter
        || this.positionFilter
        || this.statusFilter !== STATUS_OPTIONS[0].value
        || dates.start !== defaults.start
        || dates.end !== defaults.end
      )
    },
    isSalesLeave() {
      return this.$route.meta.leaveScope === 'sales'
    },
    visibleLegendItems() {
      if (!this.isSalesLeave) return this.legendItems
      return this.legendItems.filter(item => item.code !== 'parental_leave')
    },
    calendarParams() {
      return {
        from_date: this.filterDates.start,
        to_date: this.filterDates.end,
        status: this.statusFilter,
        department: this.departmentFilter ? this.departmentFilter.code : 'all',
        position: this.positionFilter ? this.positionFilter.code : 'all',
      }
    },
    detailEvent() {
      return this.detail && this.detail.event
    },
    detailTitle() {
      return this.detailSourceType === 'request' ? 'Leave request' : 'Approved leave'
    },
    hasDetailActions() {
      const permissions = this.detail && this.detail.permissions
      return Boolean(permissions && (permissions.approve || permissions.reject))
    },
    employeePosition() {
      if (!this.detailEvent) return ''
      return [this.detailEvent.position && this.detailEvent.position.name, this.detailEvent.department && this.detailEvent.department.name]
        .filter(Boolean)
        .join(' · ')
    },
    statusName() {
      if (!this.detailEvent) return ''
      if (this.detailEvent.status === 'requested') {
        return this.detailEvent.kind === 'change_dates' ? 'Date change requested' : 'Requested'
      }
      return this.detailEvent.change_pending ? 'Approved · Change pending' : 'Approved'
    },
    annual() {
      const impact = this.detail && this.detail.impact
      return impact && impact.kind === 'paid_annual' ? impact.annual : null
    },
    statusTone() {
      if (!this.detailEvent) return 'neutral'
      if (this.detailEvent.change_pending) return 'amber'
      return this.detailEvent.status === 'requested' ? 'blue' : 'green'
    },
    proofPresentation() {
      return getProofPresentation(this.detailEvent)
    },
  },
  created() {
    this.fetchFilterOptions()
    this.fetchItems()
  },
  beforeDestroy() {
    this.isActive = false
    this.fetchRequestId += 1
    this.detailRequestId += 1
  },
  methods: {
    clearFilters() {
      this.filterDates = createDefaultDates()
      this.statusFilter = STATUS_OPTIONS[0].value
      this.departmentFilter = null
      this.positionFilter = null
      this.datePickerError = false
      this.fetchItems()
    },
    fetchFilterOptions() {
      getEmployeesDepartmentList()
        .then(response => {
          if (!this.isActive) return
          this.departmentOptions = Array.isArray(response.data) ? response.data : []
        })
        .catch(() => {})
      getEmployeesPositionList()
        .then(response => {
          if (!this.isActive) return
          this.positionOptions = Array.isArray(response.data) ? response.data : []
        })
        .catch(() => {})
    },
    fetchItems() {
      if (!this.isActive || !this.filterDates || !this.filterDates.start || !this.filterDates.end) {
        return Promise.resolve()
      }
      const requestId = ++this.fetchRequestId
      this.loading = true
      this.loadError = ''
      const request = this.isSalesLeave ? getEmployeesSalesLeavesCalendarV2 : getEmployeesLeavesCalendarV2
      return request(this.calendarParams)
        .then(response => {
          if (requestId !== this.fetchRequestId) return
          const events = response.data && Array.isArray(response.data.events) ? response.data.events : []
          this.schedule = buildLeaveSchedule(events, this.filterDates)
        })
        .catch(() => {
          if (requestId === this.fetchRequestId) this.loadError = 'Unable to load leave schedule. Please try again.'
        })
        .finally(() => {
          if (requestId === this.fetchRequestId) this.loading = false
        })
    },
    onDatesHidden() {
      if (this.filterDates && this.filterDates.start && this.filterDates.end) {
        this.datePickerError = false
        this.fetchItems()
      } else this.datePickerError = true
    },
    openEvent(event) {
      const requestId = ++this.detailRequestId
      this.detail = null
      this.detailSourceType = event.source_type
      this.detailLoading = true
      this.resetDecisionState()
      this.$modal.show('leave-schedule-detail')
      const request = this.isSalesLeave ? getEmployeesSalesLeaveScheduleEventDetail : getEmployeesLeaveScheduleEventDetail
      request(event.source_type, event.source_id)
        .then(response => {
          if (requestId === this.detailRequestId) this.detail = response.data
        })
        .catch(() => {
          if (requestId === this.detailRequestId) this.$modal.hide('leave-schedule-detail')
        })
        .finally(() => {
          if (requestId === this.detailRequestId) this.detailLoading = false
        })
    },
    closeDetail() {
      if (!this.actionLoading) this.$modal.hide('leave-schedule-detail')
    },
    onDetailClosed() {
      this.detailRequestId += 1
      this.detail = null
      this.detailSourceType = null
      this.detailLoading = false
      this.resetDecisionState()
    },
    resetDecisionState() {
      this.rejectMode = false
      this.rejectSubmitted = false
      this.rejectionReason = ''
    },
    approveRequest() {
      const event = this.detailEvent
      const request = this.isSalesLeave ? postEmployeesSalesLeaveRequestApprove : postEmployeesLeaveRequestApprove
      this.runAction(
        () => request(event.source_id),
        'approveButton',
        'Leave request approved',
        event.id
      )
    },
    rejectRequest() {
      this.rejectSubmitted = true
      if (!this.rejectionReason.trim()) return
      const event = this.detailEvent
      const request = this.isSalesLeave ? postEmployeesSalesLeaveRequestReject : postEmployeesLeaveRequestReject
      this.runAction(
        () => request(event.source_id, {
          decision_reason: this.rejectionReason.trim(),
        }),
        'rejectButton',
        'Leave request rejected',
        event.id
      )
    },
    runAction(requestFactory, ref, message, eventId) {
      if (this.actionLoading) return
      this.actionLoading = true
      this.setButtonPreload(ref, true)
      Promise.resolve()
        .then(requestFactory)
        .then(() => {
          if (!this.isActive) return
          this.removeScheduleEvent(eventId)
          return this.fetchItems()
        })
        .then(() => {
          if (!this.isActive) return
          this.$modal.hide('leave-schedule-detail')
          this.$notify({ type: 'success', title: message })
        })
        .catch(() => {})
        .finally(() => {
          if (!this.isActive) return
          this.actionLoading = false
          this.setButtonPreload(ref, false)
        })
    },
    removeScheduleEvent(eventId) {
      if (!this.schedule || !eventId) return
      const events = this.schedule.events.filter(event => event.id !== eventId)
      this.schedule = buildLeaveSchedule(events, this.filterDates)
    },
    setButtonPreload(ref, value) {
      if (this.$refs[ref]) this.$refs[ref].preload = value
    },
    formatDate(value) { return value ? moment(value).format('DD MMM YYYY') : '-' },
    formatDateTime(value) { return value ? moment(value).format('DD MMM YYYY, HH:mm') : '-' },
    relativeDate(value) { return formatRelativeDate(value) },
    legendStyle(color) {
      return { backgroundColor: colorToRgba(color, 0.4), borderColor: color }
    },
  },
}
</script>
